import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { LoadingState } from "@/components/shared/LoadingState";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider } from "@/contexts/TenantContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthCallbackHandler } from "@/components/contractor-portal/AuthCallbackHandler";
import { usePushNotificationHandler } from "@/hooks/usePushNotificationHandler";
import { usePeriodicSyncHandler } from "@/hooks/usePeriodicSyncHandler";
import { useState } from "react";
// Eager-loaded routes
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Leads from "./pages/Leads";
import Contacts from "./pages/Contacts";
import Companies from "./pages/Companies";
import Divisions from "./pages/Divisions";
import Projects from "./pages/Projects";
import Calendar from "./pages/Calendar";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Tasks from "./pages/Tasks";

// Lazy-loaded heavy routes
const AccountPlanning = lazy(() => import("./pages/AccountPlanning"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Opportunities = lazy(() => import("./pages/Opportunities"));

import OpportunityBackfill from "./pages/OpportunityBackfill";
import Tenants from "./pages/Tenants";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";
import Share from "./pages/Share";
import ContractorPortalAuth from "./pages/ContractorPortalAuth";
import ContractorPortalDashboard from "./pages/ContractorPortalDashboard";
import ContractorPortalDocuments from "./pages/ContractorPortalDocuments";
import ContractorPortalProfile from "./pages/ContractorPortalProfile";
import ContractorPortalRatings from "./pages/ContractorPortalRatings";
import PartnerAnalytics from "./pages/PartnerAnalytics";
import Contracts from "./pages/Contracts";
import VerifyInvitation from "./pages/VerifyInvitation";
import SetupPassword from "./pages/SetupPassword";

// Lazy load PWA components to avoid race condition with virtual modules
const ServiceWorkerRegistration = lazy(() => import("@/components/pwa/ServiceWorkerRegistration").then(m => ({ default: m.ServiceWorkerRegistration })));
const OfflineIndicator = lazy(() => import("@/components/pwa/OfflineIndicator").then(m => ({ default: m.OfflineIndicator })));
const InstallPrompt = lazy(() => import("@/components/pwa/InstallPrompt").then(m => ({ default: m.InstallPrompt })));
const SyncStatusBanner = lazy(() => import("@/components/pwa/SyncStatusBanner").then(m => ({ default: m.SyncStatusBanner })));
const WelcomeScreen = lazy(() => import("@/components/pwa/WelcomeScreen").then(m => ({ default: m.WelcomeScreen })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const APP_CACHE_VERSION = '1.0.2'; // Increment to invalidate old caches

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: `REACT_QUERY_OFFLINE_CACHE_v${APP_CACHE_VERSION}`,
  throttleTime: 1000, // Prevent rapid writes
});

// Welcome screen handler
function WelcomeScreenHandler() {
  const navigate = useNavigate();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const welcomeCompleted = localStorage.getItem('pwa-welcome-completed');

  useEffect(() => {
    // Show welcome screen only for first-time installs
    if (isStandalone && !welcomeCompleted && window.location.pathname === '/') {
      navigate('/welcome');
    }
  }, [isStandalone, welcomeCompleted, navigate]);

  return null;
}

function AppRoutes() {
  const [pwaMounted, setPwaMounted] = useState(false);
  
  // Handle push notification clicks
  usePushNotificationHandler();
  
  // Handle periodic background sync
  usePeriodicSyncHandler();

  // Defer PWA component mounting until after initial render
  useEffect(() => {
    const timer = setTimeout(() => setPwaMounted(true), 0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <WelcomeScreenHandler />
      {pwaMounted && !import.meta.env.DEV && (
        <Suspense fallback={null}>
          <ServiceWorkerRegistration />
          <SyncStatusBanner />
          <OfflineIndicator />
          <InstallPrompt />
        </Suspense>
      )}
      <Routes>
        <Route path="/" element={<ErrorBoundary><Index /></ErrorBoundary>} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/verify-invitation" element={<VerifyInvitation />} />
        <Route path="/auth/setup-password" element={<SetupPassword />} />
        <Route path="/leads" element={<ErrorBoundary><Leads /></ErrorBoundary>} />
        <Route path="/contacts" element={<ErrorBoundary><Contacts /></ErrorBoundary>} />
        <Route path="/companies" element={<ErrorBoundary><Companies /></ErrorBoundary>} />
        <Route path="/partners/analytics" element={<ErrorBoundary><PartnerAnalytics /></ErrorBoundary>} />
        <Route path="/contracts" element={<ErrorBoundary><Contracts /></ErrorBoundary>} />
        <Route path="/divisions" element={<ErrorBoundary><Divisions /></ErrorBoundary>} />
        <Route path="/projects" element={<ErrorBoundary><Projects /></ErrorBoundary>} />
        <Route path="/calendar" element={<ErrorBoundary><Calendar /></ErrorBoundary>} />
        <Route path="/reports" element={<ErrorBoundary><Reports /></ErrorBoundary>} />
        <Route path="/account-planning" element={<ErrorBoundary><Suspense fallback={<LoadingState />}><AccountPlanning /></Suspense></ErrorBoundary>} />
        <Route path="/inbox" element={<ErrorBoundary><Suspense fallback={<LoadingState />}><Inbox /></Suspense></ErrorBoundary>} />
        <Route path="/tasks" element={<ErrorBoundary><Tasks /></ErrorBoundary>} />
        <Route path="/opportunities" element={<ErrorBoundary><Suspense fallback={<LoadingState />}><Opportunities /></Suspense></ErrorBoundary>} />
        <Route path="/opportunities/backfill" element={<ErrorBoundary><OpportunityBackfill /></ErrorBoundary>} />
        <Route path="/tenants" element={<ErrorBoundary><Tenants /></ErrorBoundary>} />
        <Route path="/settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
        <Route path="/contractor-portal/auth" element={<ContractorPortalAuth />} />
        <Route path="/contractor-portal/dashboard" element={<ContractorPortalDashboard />} />
        <Route path="/contractor-portal/documents" element={<ContractorPortalDocuments />} />
        <Route path="/contractor-portal/profile" element={<ContractorPortalProfile />} />
        <Route path="/contractor-portal/ratings" element={<ContractorPortalRatings />} />
        <Route path="/install" element={<Install />} />
        <Route path="/welcome" element={<Suspense fallback={<LoadingState />}><WelcomeScreen /></Suspense>} />
        <Route path="/share" element={<Share />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 1000 * 60 * 60 * 24, // 24 hours
      buster: APP_CACHE_VERSION, // Force invalidation on version change
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => {
          // Don't persist queries with stale/error status or empty data
          if (query.state.status !== 'success') return false;
          if (!query.state.data) return false;
          if (Array.isArray(query.state.data) && query.state.data.length === 0) return false;
          
          // Don't persist opportunities - let it always fetch fresh to avoid race conditions
          const queryKey = query.queryKey[0] as string;
          return ['contacts', 'companies', 'tasks', 'emails'].includes(queryKey);
        },
      },
    }}
  >
    <TooltipProvider>
      <AuthProvider>
        <AuthCallbackHandler />
        <TenantProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ErrorBoundary>
        </TenantProvider>
      </AuthProvider>
    </TooltipProvider>
  </PersistQueryClientProvider>
);

export default App;
